import { type FC } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/** localStorage key remembering which Play sub-section the user visited last. */
export const LAST_PLAY_SECTION_KEY = 'gdc.lastPlaySection';

/** The only values `/play` may redirect to; anything else stored is ignored. */
export const PLAY_SECTIONS = ['/battles', '/rosters', '/near-me', '/tools'] as const;

/** Remember the Play sub-section for `pathname` (no-op elsewhere; storage failures are ignored). */
export function rememberPlaySection(pathname: string): void {
  const section = PLAY_SECTIONS.find((path) => pathname === path || pathname.startsWith(`${path}/`));
  if (!section) return;
  try {
    localStorage.setItem(LAST_PLAY_SECTION_KEY, section);
  } catch {
    // Private windows and blocked storage: remembering is a convenience only
  }
}

/** The last visited Play sub-section, or `/battles` when unknown, unavailable or tampered with. */
export function readLastPlaySection(): string {
  try {
    const stored = localStorage.getItem(LAST_PLAY_SECTION_KEY);
    return PLAY_SECTIONS.find((path) => path === stored) ?? '/battles';
  } catch {
    return '/battles';
  }
}

/** `/play` -> the last Play sub-section visited. */
export const PlayRedirect: FC = () => <Navigate to={readLastPlaySection()} replace />;

const PROFILE_TABS: Record<string, string> = {
  friends: '/you/friends',
  achievements: '/you/achievements',
  stats: '/you/stats',
};

/** `/profile?tab=…` -> the matching `/you/*` page; unknown tabs land on `/you/profile`. Other params and the hash are kept. */
export const ProfileRedirect: FC = () => {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  params.delete('tab');
  const rest = params.toString();
  const target = (tab && Object.prototype.hasOwnProperty.call(PROFILE_TABS, tab) ? PROFILE_TABS[tab] : '/you/profile');
  return <Navigate to={`${target}${rest ? `?${rest}` : ''}${hash}`} replace />;
};

/** `/settings#privacy` -> `/you/settings#privacy` (search and hash preserved). */
export const SettingsRedirect: FC = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/you/settings${search}${hash}`} replace />;
};
